/**
 * Base code for the type of a clinical impression resource
 */
export enum ClinicalImpressionCode {
  Routine = 'clinicalimpression.code.routine',
  ClinicalUpdate = 'clinicalimpression.code.clinicalupdate',
  Handover = 'clinicalimpression.code.handover',
  Onboarding = 'clinicalimpression.code.onboarding',
  Authorization = 'clinicalimpression.code.authorization',
  ClinicalCallout = 'clinicalimpression.code.clinicalcallout',
  Discharge = 'clinicalimpression.code.discharge',
  BloodResults = 'clinicalimpression.code.bloodresults',
  Physiotherapy = 'clinicalimpression.code.physiotherapy',
  DailySummary = 'clinicalimpression.code.dailysummary',
  OverallSummary = 'clinicalimpression.code.overallsummary',
  PreOnboarding = 'clinicalimpression.code.preonboarding',
}

/**
 * Code to identify the location for the clinical impression
 */
export enum ClinicalImpressionLocationCode {
  Virtual = 'clinicalimpression.code.location.virtual',
  InPerson = 'clinicalimpression.code.location.inperson',
}

/**
 * Codes for the note code extensions of a clinical impression
 */
export enum ClinicalImpressionNoteCode {
  Subjective = 'clinicalimpression.note.extension.code.soap.subjective',
  Objective = 'clinicalimpression.note.extension.code.soap.objective',
  Plan = 'clinicalimpression.note.extension.code.soap.plan',
  Assessment = 'clinicalimpression.note.extension.code.soap.assessment',
  DailySummary = 'clinicalimpression.note.extension.code.soap.dailysummary',
  OverallSummary = 'clinicalimpression.note.extension.code.soap.overallsummary',
  PreOnboarding = 'clinicalimpression.note.extension.code.soap.preonboarding',
}

/**
 * Codes for structured clinical summary sections
 */
export enum ClinicalSummarySectionCode {
  HistoryAndExamination = 'clinicalimpression.summary.section.historyandexamination',
  Investigations = 'clinicalimpression.summary.section.investigations',
  Diagnoses = 'clinicalimpression.summary.section.diagnoses',
  Management = 'clinicalimpression.summary.section.management',
  Complications = 'clinicalimpression.summary.section.complications',
  HospitalEscalations = 'clinicalimpression.summary.section.hospitalescalations',
  Procedures = 'clinicalimpression.summary.section.procedures',
  FutureManagement = 'clinicalimpression.summary.section.futuremanagement',
  MedicationChanges = 'clinicalimpression.summary.section.medicationchanges',
  MedicationsToTakeHome = 'clinicalimpression.summary.section.medicationstotakehome',
  AllergiesAndAdverseReactions = 'clinicalimpression.summary.section.allergiesandadversereactions',
  InformationForPatient = 'clinicalimpression.summary.section.informationforpatient',
}

export const clinicalSummarySectionCodeDisplay: Record<
  ClinicalSummarySectionCode,
  string
> = {
  [ClinicalSummarySectionCode.HistoryAndExamination]: 'History and Examination',
  [ClinicalSummarySectionCode.Investigations]: 'Investigations',
  [ClinicalSummarySectionCode.Diagnoses]: 'Diagnoses',
  [ClinicalSummarySectionCode.Management]: 'Management',
  [ClinicalSummarySectionCode.Complications]: 'Complications',
  [ClinicalSummarySectionCode.HospitalEscalations]: 'Hospital Escalations',
  [ClinicalSummarySectionCode.Procedures]: 'Procedures',
  [ClinicalSummarySectionCode.FutureManagement]: 'Future Management',
  [ClinicalSummarySectionCode.MedicationChanges]: 'Medication Changes',
  [ClinicalSummarySectionCode.MedicationsToTakeHome]:
    'Medications to Take Home',
  [ClinicalSummarySectionCode.AllergiesAndAdverseReactions]:
    'Allergies and Adverse Reactions',
  [ClinicalSummarySectionCode.InformationForPatient]: 'Information for Patient',
};

/**
 * Summary code value associated with the clinical impression
 */
export enum ClinicalImpressionAssessmentValueCode {
  Stable = 'clinicalimpression.code.soap.assessment.stable',
  Deteriorating = 'clinicalimpression.code.soap.assessment.deteriorating',
  Improving = 'clinicalimpression.code.soap.assessment.improving',
  Demised = 'clinicalimpression.code.soap.assessment.demised',
}

export const clinicalImpressionAssessmentValueCodeDisplay: Record<
  ClinicalImpressionAssessmentValueCode,
  string
> = {
  [ClinicalImpressionAssessmentValueCode.Stable]: 'Stable',
  [ClinicalImpressionAssessmentValueCode.Deteriorating]: 'Deteriorating',
  [ClinicalImpressionAssessmentValueCode.Improving]: 'Improving',
  [ClinicalImpressionAssessmentValueCode.Demised]: 'Demised',
};

/**
 * Summary code value associated with the clinical impression
 */
export enum ClinicalImpressionPlanValueCode {
  Continue = 'clinicalimpression.code.soap.plan.continue',
  Amend = 'clinicalimpression.code.soap.plan.amend',
  Discharge = 'clinicalimpression.code.soap.plan.discharge',
  Escalate = 'clinicalimpression.code.soap.plan.escalate',
  UpgradedToHAH = 'clinicalimpression.code.soap.plan.upgradedToHAH',
  DeclinedOnboarding = 'clinicalimpression.code.soap.plan.declinedonboarding',
}

export const clinicalImpressionPlanValueCodeDisplay: Record<
  ClinicalImpressionPlanValueCode,
  string
> = {
  [ClinicalImpressionPlanValueCode.Continue]: 'Continue',
  [ClinicalImpressionPlanValueCode.Amend]: 'Amend',
  [ClinicalImpressionPlanValueCode.Discharge]: 'Discharge',
  [ClinicalImpressionPlanValueCode.Escalate]: 'Escalate',
  [ClinicalImpressionPlanValueCode.UpgradedToHAH]:
    'Upgraded to hospital at home',
  [ClinicalImpressionPlanValueCode.DeclinedOnboarding]: 'Declined Onboarding',
};

/**
 * Code to indicate the purpose of an investigation
 */
export enum ClinicalImpressionInvestigationCode {
  LatestVitals = 'clinicalimpression.investigation.code.latestvitals',
  Media = 'clinicalimpression.investigation.code.media',
}
